import Swiper from 'swiper';
import { Navigation, EffectFade, Thumbs, Zoom, Manipulation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';
import 'swiper/css/zoom';
import { classInstance } from '@/shared/helpers/helpers';
import { vars } from '@/app/config/variables';

export class ProductSwiper {
	constructor(selector, options = {}) {
		this.$container = selector;
		this.active = 0;
		this.open = false;
		this.tablet = vars.breakpoint.tablet;
		this.desktop = vars.breakpoint.laptop;
		this.view = null;
		this.hoverableThumbs = false;

		if (!this.$container) return;

		this.zoom = options.zoom || 0;
		this.selectorMain = options.selectorMain || '.product-swiper-main';
		this.selectorThumbWrapper = options.selectorThumbWrapper || '.product-swiper-thumb-wrapper';
		this.selectorThumb = options.selectorThumb || '.product-swiper-thumb';
		this.selectorModalMain = options.selectorModalMain || '.modal-swiper-main';
		this.selectorModalThumb = options.selectorModalThumb || '.modal-swiper-thumb';
		this.selectorPagination = options.selectorPagination || '.swiper-pagination';
		this.selectorZoom = options.selectorZoom || '.swiper-zoom-container';
		this.selectorNext = options.selectorNext || '.swiper-next';
		this.selectorPrev = options.selectorPrev || '.swiper-prev';
		this.selectorModal = options.selectorModal || '#modal-product-gallery';
		
		this.init();
	}

	init() {
		classInstance.set(this.$container, { swiper: this });
		
		this.$main = this.$container.querySelector(this.selectorMain);

		this.$thumb = this.$container.querySelector(this.selectorThumb);
		this.$thumbWrapper = this.$container.querySelector(this.selectorThumbWrapper);
		
		this.$pagination = this.$container.querySelector(this.selectorPagination);

		this.$elemensMain = this.$main.querySelectorAll('.swiper-slide');
		this.$elemensThumb = this.$thumb.querySelectorAll('.swiper-slide');

		this.fragmentMain = new DocumentFragment();
		this.fragmentThumb = new DocumentFragment();

		this.$elemensMain.forEach((element) => this.fragmentMain.appendChild(element.cloneNode(true)));
		this.$elemensThumb.forEach((element) => this.fragmentThumb.appendChild(element.cloneNode(true)));

		this.swiperThumb = new Swiper(this.$thumb, {
			modules: [Navigation, Manipulation],
			slidesPerView: 'auto',
			spaceBetween: 6,
			direction: 'horizontal',
			navigation: {
				prevEl: this.$thumbWrapper.querySelector(this.selectorPrev),
				nextEl: this.$thumbWrapper.querySelector(this.selectorNext),
			},
			breakpoints: {
				[this.tablet]: {
					direction: 'vertical',
				},
				[this.desktop]: {
					direction: 'horizontal',
				},
			},
		});

		this.swiperMain = new Swiper(this.$main, {
			modules: [Navigation, EffectFade, Thumbs, Zoom, Manipulation],
			slidesPerView: 1,
			spaceBetween: 0,
			rewind: false,
			effect: 'fade',
			fadeEffect: {
				crossFade: true,
			},
			zoom: false,
			navigation: {
				prevEl: this.$main.querySelector(this.selectorPrev),
				nextEl: this.$main.querySelector(this.selectorNext),
			},
			thumbs: {
				swiper: this.swiperThumb,
			},
			on: {
				init: (swiper) => {
					requestAnimationFrame(() => {
						this.checkVideo(swiper, undefined, swiper.activeIndex);
					});
				},
			},
		});

		this.renderModal();
		this.zoom > 0 && this.checkZoom(true);

		this.openHandler = this.openHandler.bind(this);
		this.$main.addEventListener('click', this.openHandler);

		this.changeHandler = this.changeHandler.bind(this);
		this.swiperMain.on('slideChange', this.changeHandler);

		this.slideHandler = this.slideHandler.bind(this);
		this.hoverableThumbs &&
			this.swiperThumb.slides.forEach((slide) => {
				slide.addEventListener('mouseenter', this.slideHandler);
			});

		this.zoomInHandler = this.zoomInHandler.bind(this);
		this.$main.addEventListener('mouseenter', this.zoomInHandler);

		this.zoomOutHandler = this.zoomOutHandler.bind(this);
		this.$main.addEventListener('mouseleave', this.zoomOutHandler);

		this.moveHeandler = this.moveHeandler.bind(this);
		this.$main.addEventListener('mousemove', this.moveHeandler);

		this.resizeHeandler = this.resizeHeandler.bind(this);
		window.addEventListener('resize', this.resizeHeandler);
	}

	initModal() {
		const modalMain = this.modal.querySelector(this.selectorModalMain);
		const modalThumb = this.modal.querySelector(this.selectorModalThumb);

		modalMain.querySelector('.swiper-wrapper').append(this.fragmentMain);
		modalThumb.querySelector('.swiper-wrapper').append(this.fragmentThumb);

		// disable controls for video and replay
		const modalVideo = modalMain.querySelector('.swiper-video-container video');
		if (modalVideo) {
			modalVideo.controls = false;
			modalVideo.muted = false;
			modalVideo.loop = true;
		}

		this.swiperThumbModal = new Swiper(modalThumb, {
			modules: [Manipulation],
			slidesPerView: 'auto',
			spaceBetween: 6,
		});

		this.swiperMainModal = new Swiper(modalMain, {
			modules: [Navigation, EffectFade, Thumbs, Zoom, Manipulation],
			slidesPerView: 1,
			spaceBetween: 0,
			rewind: false,
			effect: 'fade',
			fadeEffect: {
				crossFade: true,
			},
			zoom: false,
			navigation: {
				prevEl: modalMain.querySelector(this.selectorPrev),
				nextEl: modalMain.querySelector(this.selectorNext),
			},
			thumbs: {
				swiper: this.swiperThumbModal,
			},
		});

		this.closeHandler = this.closeHandler.bind(this);
		this.modal.addEventListener('modalClose', this.closeHandler);

		this.changeModalHandler = this.changeModalHandler.bind(this);
		this.swiperMainModal.on('slideChange', this.changeModalHandler);

		this.slideModalHandler = this.slideModalHandler.bind(this);
		this.hoverableThumbs &&
			this.swiperThumbModal.slides.forEach((slide) => {
				slide.addEventListener('mouseenter', this.slideModalHandler);
			});
	}

	changeHandler(swiper) {
		this.active = swiper.activeIndex;
		if (!this.open) this.checkVideo(this.swiperMain, swiper.previousIndex, swiper.activeIndex);
	}

	changeModalHandler(swiper) {
		this.active = swiper.activeIndex;
		if (this.open) this.checkVideo(this.swiperMainModal, swiper.previousIndex, swiper.activeIndex);
	}

	slideHandler(event) {
		const index = this.swiperThumb.slides.indexOf(event.target);
		this.swiperThumb.slideTo(index);
		this.swiperMain.slideTo(index);
	}

	slideModalHandler(event) {
		const index = this.swiperThumbModal.slides.indexOf(event.target);
		this.swiperThumbModal.slideTo(index);
		this.swiperMainModal.slideTo(index);
	}

	openHandler(event) {
		const target = event.target;
		if (target.closest(`${this.selectorNext}, ${this.selectorPrev}, .swiper-button-disabled`)) {
			return;
		}

		const slide = this.swiperMain.slides[this.swiperMain.activeIndex];
		this.swiperThumbModal.slideTo(this.active);
		this.swiperMainModal.slideTo(this.active);
		window.app.modal.open(this.selectorModal);
		this.open = true;
		if (this.isVideo(slide)) {
			this.checkVideo(this.swiperMain, this.active);
			this.checkVideo(this.swiperMainModal, this.active, this.active);
		}
	}

	closeHandler() {
		const mainVideo = this.swiperMain.slides[this.active]?.querySelector('video');

		this.checkVideo(this.swiperMainModal, this.active);
		this.swiperThumb.slideTo(this.active);
		this.swiperMain.slideTo(this.active);
		window.app.modal.close();
		this.open = false;

		if (mainVideo) {
			mainVideo.play().catch(() => {});
		}
	}

	zoomInHandler() {
		const target = this.swiperMain.slides[this.swiperMain.activeIndex];
		if (!this.isVideo(target) && this.view === 'desktop') {
			this.zoomIn(this.swiperMain);
		}
	}

	zoomOutHandler() {
		const target = this.swiperMain.slides[this.swiperMain.activeIndex];
		if (!this.isVideo(target) && this.view === 'desktop') {
			this.zoomOut(this.swiperMain);
		}
	}

	moveHeandler(event) {
		const target = this.swiperMain.slides[this.swiperMain.activeIndex];
		if (!this.isVideo(target) && this.view === 'desktop') {
			this.zoomMove(this.swiperMain, event.clientX, event.clientY);
		}
	}
	
	isVideo(slide) {
		return slide && slide.classList.contains('swiper-slide-video');
	}

	resizeHeandler() {
		this.checkZoom();
	}

	zoomIn(swiper) {
		swiper.zoom.in(this.zoom);
	}

	zoomOut(swiper) {
		swiper.zoom.out();
	}

	zoomMove(swiper, clientX, clientY) {
		const element = swiper.slides[this.active].querySelector(this.selectorZoom);
		const rect = swiper.slides[this.active].getBoundingClientRect();

		const translateX = this.getTranslate(clientX - rect.left, rect.width, this.zoom);
		const translateY = this.getTranslate(clientY - rect.top, rect.height, this.zoom);

		if (element) element.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
	}

	checkVideo(swiper, pauseIndex, playIndex) {
		if (pauseIndex !== undefined) {
			const v = swiper.slides[pauseIndex]?.querySelector('video');
			if (v) v.pause();
		}

		if (playIndex !== undefined) {
			const v = swiper.slides[playIndex]?.querySelector('video');
			if (v) {
				v.play().catch(() => {});
			}
		}
	}

	checkZoom(init = false) {
		const desktop = window.innerWidth >= this.desktop;
		if (init) this.view = desktop ? 'mobile' : 'desktop';

		if (this.view === 'mobile' && desktop) {
			this.swiperMain.zoom.enable();
			this.swiperMainModal.zoom.disable();
			this.view = 'desktop';
		}
		if (this.view === 'desktop' && !desktop) {
			this.swiperMain.zoom.disable();
			this.swiperMainModal.zoom.enable();
			this.view = 'mobile';
		}
	}

	getTranslate(position, size, scale) {
		return Math.round((size / 2 - position) * scale * ((1 / scale) * (scale - 1)));
	}

	renderModal() {
		this.createModal();
		this.initModal();
	}

	createModal() {
		this.modal = document.querySelector(this.selectorModal);
		const container = this.modal.querySelector('.modal-product-gallery-container');
		const modalTitle = this.modal.querySelector('.modal-window__title');
		const pageTitle = document.querySelector('.title-page');
		if (pageTitle) modalTitle.textContent = pageTitle.textContent;
		container.innerHTML = `
			${this.createModalSwiperMain()}
			${this.createModalSwiperThumb()}
		`;
	}

	createModalSwiperMain() {
		return `
			<div class="swiper modal-swiper-main">
				<div class="swiper-wrapper"></div>
				<div class="swiper-pagination"></div>
				<button class="swiper-prev swiper-button-prev" type="button">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M9.57 5.92993L3.5 11.9999L9.57 18.0699" stroke="currentColor" stroke-opacity="0.85" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M20.4999 12H3.66992" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</button>
				<button class="swiper-next swiper-button-next" type="button">
					<svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M14.9297 5.92993L20.9997 11.9999L14.9297 18.0699" stroke="currentColor" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M4 12H20.83" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</button>
			</div>
		`;
	}

	createModalSwiperThumb() {
		return `
			<div class="swiper modal-swiper-thumb">
				<div class="swiper-wrapper"></div>
			</div>
		`;
	}
	
	update(offer, withVideo = true) {
		this.updateMain(offer, withVideo);
		this.updateThumb(offer, withVideo);
	}

	updateMain(offer, withVideo = true) {
		const slides = this.filtered(offer, this.$elemensMain, withVideo);
		const modalSlides = [];
		
		this.swiperMain.removeAllSlides();
		this.swiperMain.appendSlide(slides);
		this.swiperMain.updateSlides();
		this.swiperMain.slideTo(0);
		
		if (this.modal) {
			slides.forEach((element) => modalSlides.push(element.cloneNode(true)));
			this.swiperMainModal.removeAllSlides();
			this.swiperMainModal.appendSlide(modalSlides);
			this.swiperMainModal.updateSlides();
			this.swiperMainModal.slideTo(0);
		}
	}

	updateThumb(offer, withVideo = true) {
		const slides = this.filtered(offer, this.$elemensThumb, withVideo);
		const modalSlides = [];
		
		this.swiperThumb.removeAllSlides();
		this.swiperThumb.appendSlide(slides);
		this.swiperThumb.updateSlides();
		this.swiperThumb.slideTo(0);

		if (this.modal) {
			slides.forEach((element) => modalSlides.push(element.cloneNode(true)));
			this.swiperThumbModal.removeAllSlides();
			this.swiperThumbModal.appendSlide(modalSlides);
			this.swiperThumbModal.updateSlides();
			this.swiperThumbModal.slideTo(0);
		}
	}

	filtered(value, slides, withVideo = true) {
		return Array.from(slides).filter((item) => item.dataset.offer === value || (withVideo && this.isVideo(item)));
	}
}
